import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Users, User, Bot, Zap, ShieldOff, Search, ArrowLeft, Clock, Globe,
  CheckCircle, Ban, RefreshCw, ChevronDown, ChevronLeft, ChevronRight, Copy,
  AlertTriangle, Shield, Activity, Link2, BarChart2, GitBranch, Loader2,
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { cn } from "../../../lib/utils";
import { useToast } from "../../../../hooks/use-toast";
import GatewayStatCard from "../components/GatewayStatCard";
import GatewayStatusBadge from "../components/GatewayStatusBadge";
import GatewaySensitivityBadge from "../components/GatewaySensitivityBadge";
import { Button } from "../../../ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "../../../ui/popover";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../../ui/dialog";
import GatewayTimeline from "../components/GatewayTimeline";
import GatewayFilterBar from "../components/GatewayFilterBar";
import {
  GatewayHumanUsersApi,
  GatewayNhisApi,
  GatewayAuditApi,
  GatewayPostProcessorApi,
} from "../Api/gatewayApi";
import {
  formatTimestamp,
  formatDuration,
  identityStatusColor,
  copyToClipboard,
  apiError,
} from "../utils/gatewayUtils";
import "../../ManagementDialog.css";
import "../../CloudAnalytics/CloudEntityAnalytics.css";
import "../AgenticGateway.css";


type ListTab = "humans" | "nhis" | "active";
type DetailTab = "overview" | "analytics" | "sessions" | "lineage" | "risk";
type IdentityType = "human" | "nhi";

interface HumanSummary {
  totalHumans?: number;
  activeToday?: number;
  activeNow?: number;
  blocked?: number;
}

interface NhiSummary {
  totalNhis?: number;
  activeToday?: number;
  activeNow?: number;
  blocked?: number;
}


function ColoredJson({ data }: { data: any }) {
  const json = JSON.stringify(data, null, 2);
  const colored = json.replace(
    /("(?:\\.|[^"\\])*")\s*:/g, // keys
    '<span class="text-primary font-medium">$1</span>:'
  ).replace(
    /:\s*("(?:\\.|[^"\\])*")/g,
    ': <span class="text-emerald-600 dark:text-emerald-400">$1</span>'
  ).replace(
    /:\s*(\d+\.?\d*)/g,
    ': <span class="text-amber-600 dark:text-amber-400">$1</span>'
  ).replace(
    /:\s*(true|false)/g,
    ': <span class="text-violet-600 dark:text-violet-400">$1</span>'
  ).replace(
    /:\s*(null)/g,
    ': <span class="text-red-600 dark:text-red-400 italic">$1</span>'
  );
  return (
    <pre
      className="text-xs text-foreground/70 max-h-64 overflow-auto whitespace-pre leading-relaxed"
      dangerouslySetInnerHTML={{ __html: colored }}
    />
  );
}

function IdentityStatusBadge({ status }: { status: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center px-2 py-0 rounded-md text-[10px] font-medium border",
        identityStatusColor(status)
      )}
    >
      {status}
    </span>
  );
}

function RoleBadges({ roles }: { roles: string[] }) {
  if (!roles?.length)
    return <span className="text-xs text-muted-foreground">No roles</span>;
  const visible = roles.slice(0, 5);
  const overflow = roles.length - 5;
  return (
    <span className="flex flex-wrap gap-1">
      {visible.map((r) => (
        <span
          key={r}
          className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-sky-500/10 text-sky-600 dark:text-sky-400 border border-sky-500/20"
        >
          {r}
        </span>
      ))}
      {overflow > 0 && (
        <span className="text-xs text-muted-foreground">+{overflow}</span>
      )}
    </span>
  );
}


function SessionDrillDown({ sessionId }: { sessionId: string }) {
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    GatewayAuditApi.sessionEvents(sessionId)
      .then((data: any) => {
        const evts = data?.content || data?.logs || data || [];
        setEvents(Array.isArray(evts) ? evts : []);
      })
      .catch((e: any) => setError(apiError(e)))
      .finally(() => setLoading(false));
  }, [sessionId]);

  if (loading)
    return (
      <div className="px-4 py-3 text-xs text-muted-foreground flex items-center gap-2">
        <RefreshCw className="w-3 h-3 animate-spin" /> Loading audit events…
      </div>
    );
  if (error)
    return (
      <div className="px-4 py-2 text-xs text-red-600 dark:text-red-400">
        Failed to load: {error}
      </div>
    );
  if (!events.length)
    return (
      <div className="px-4 py-2 text-xs text-muted-foreground">
        No audit events for this session.
      </div>
    );

  return (
    <div className="px-4 py-3 bg-card border-t border-border">
      <GatewayTimeline events={events} />
    </div>
  );
}


interface PagedSessions {
  content: any[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

interface HumanSessionFilterValues {
  status: string[];
  agentName: string[];
  authMethod: string[];
  tokenType: string[];
  ipAddress: string;
  fromDate: string;
  toDate: string;
  search: string;
}

const EMPTY_HUMAN_SESSION_FILTERS: HumanSessionFilterValues = {
  status: [],
  agentName: [],
  authMethod: [],
  tokenType: [],
  ipAddress: "",
  fromDate: "",
  toDate: "",
  search: "",
};

function SessionsTab({
  identityId,
  identityType,
}: {
  identityId: string;
  identityType: IdentityType;
}) {
  if (identityType === "human") {
    return <HumanSessionsTab userId={identityId} />;
  }
  return <NhiSessionsTab identityId={identityId} />;
}

function HumanSessionsTab({ userId }: { userId: string }) {
  const { toast } = useToast();
  const [paged, setPaged] = useState<PagedSessions>({
    content: [],
    totalElements: 0,
    totalPages: 0,
    number: 0,
    size: 15,
  });
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const [draftFilters, setDraftFilters] = useState<HumanSessionFilterValues>(EMPTY_HUMAN_SESSION_FILTERS);
  const [filters, setFilters] = useState<HumanSessionFilterValues>(EMPTY_HUMAN_SESSION_FILTERS);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(15);
  const [sort] = useState("connectedAt,desc");
  const [filterOptions, setFilterOptions] = useState<{
    statuses: string[];
    agentNames: string[];
    authMethods: string[];
    tokenTypes: string[];
  }>({ statuses: [], agentNames: [], authMethods: [], tokenTypes: [] });

  useEffect(() => {
    let cancelled = false;
    GatewayHumanUsersApi.sessionFilters(userId)
      .then((data: any) => {
        if (cancelled) return;
        setFilterOptions({
          statuses: data?.statuses || [],
          agentNames: data?.agentNames || [],
          authMethods: data?.authMethods || [],
          tokenTypes: data?.tokenTypes || [],
        });
      })
      .catch(() => {
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    setPage(0);
  }, [filters, size]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params: any = { page, size, sort };
    if (filters.status.length) params.status = filters.status;
    if (filters.agentName.length) params.agentName = filters.agentName;
    if (filters.authMethod.length) params.authMethod = filters.authMethod;
    if (filters.tokenType.length) params.tokenType = filters.tokenType;
    if (filters.ipAddress) params.ipAddress = filters.ipAddress;
    if (filters.fromDate) params.fromDate = filters.fromDate;
    if (filters.toDate) params.toDate = filters.toDate;
    if (filters.search) params.search = filters.search;

    GatewayHumanUsersApi.sessions(userId, params)
      .then((data: any) => {
        if (cancelled) return;
        setPaged({
          content: Array.isArray(data?.content) ? data.content : [],
          totalElements: data?.totalElements || 0,
          totalPages: data?.totalPages || 0,
          number: typeof data?.number === "number" ? data.number : 0,
          size: typeof data?.size === "number" ? data.size : size,
        });
      })
      .catch((e: any) => {
        if (!cancelled) {
          toast({
            title: "Failed to load sessions",
            description: apiError(e),
            variant: "destructive",
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, page, size, sort, filters]);

  const handleFilterChange = (key: string, value: string | string[]) => {
    setDraftFilters((prev) => ({ ...prev, [key]: value } as HumanSessionFilterValues));
  };
  const handleApplyFilters = () => {
    setFilters(draftFilters);
  };
  const handleClearFilters = () => {
    setDraftFilters(EMPTY_HUMAN_SESSION_FILTERS);
    setFilters(EMPTY_HUMAN_SESSION_FILTERS);
  };

  const sessions = paged.content;
  const totalElements = paged.totalElements;
  const totalPages = paged.totalPages;
  const currentPage = paged.number;
  const pageSize = paged.size || size;
  const from = totalElements === 0 ? 0 : currentPage * pageSize + 1;
  const to = Math.min((currentPage + 1) * pageSize, totalElements);

  return (
    <div className="space-y-4">
      <GatewayFilterBar
        fields={[
          { key: "status", label: "Status", type: "multiselect", options: filterOptions.statuses },
          { key: "agentName", label: "Agent", type: "multiselect", options: filterOptions.agentNames },
          { key: "authMethod", label: "Auth Method", type: "multiselect", options: filterOptions.authMethods },
          { key: "tokenType", label: "Token Type", type: "multiselect", options: filterOptions.tokenTypes },
          { key: "ipAddress", label: "IP Address", type: "ip", placeholder: "e.g. 192.168.1.* or 10.0.0.5" },
          { key: "fromDate", label: "From", type: "date" },
          { key: "toDate", label: "To", type: "date" },
          { key: "search", label: "Search", type: "text", placeholder: "Search sessions…" },
        ]}
        values={draftFilters as unknown as Record<string, string | string[]>}
        onChange={handleFilterChange}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
      />

      {loading ? (
        <div className="flex items-center gap-2 py-8 justify-center text-muted-foreground text-sm">
          <RefreshCw className="w-4 h-4 animate-spin" /> Loading sessions…
        </div>
      ) : sessions.length === 0 ? (
        <div className="text-sm text-muted-foreground py-8 text-center">
          No sessions match the current filters.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-xs text-muted-foreground uppercase tracking-wider">
                <th className="text-left py-2 pl-3 pr-4">Session ID</th>
                <th className="text-left py-2 pr-4">Agent</th>
                <th className="text-left py-2 pr-4">Auth Method</th>
                <th className="text-left py-2 pr-4">Token Type</th>
                <th className="text-center py-2 pr-4">Requests</th>
                <th className="text-left py-2 pr-4">Connected</th>
                <th className="text-left py-2 pr-4">Disconnected</th>
                <th className="text-left py-2 pr-4">IP</th>
                <th className="text-left py-2 pr-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s: any) => {
                const sid = s.sessionId || "";
                const isExpanded = expandedId === sid;
                return (
                  <React.Fragment key={sid}>
                    <tr
                      className="border-b border-border hover:bg-primary/[0.025] cursor-pointer"
                      onClick={() => setExpandedId(isExpanded ? null : sid)}
                    >
                      <td className="py-2 pl-3 pr-4">
                        <div className="flex items-center gap-1">
                          {isExpanded ? (
                            <ChevronDown className="w-3 h-3 text-muted-foreground shrink-0" />
                          ) : (
                            <ChevronRight className="w-3 h-3 text-muted-foreground shrink-0" />
                          )}
                          <span className="font-mono text-xs text-muted-foreground">
                            {sid.substring(0, 20)}…
                          </span>
                          <button
                            className="text-muted-foreground/70 hover:text-foreground/80"
                            onClick={async (e) => {
                              e.stopPropagation();
                              await copyToClipboard(sid);
                              toast({ title: "Copied to clipboard" });
                            }}
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                        </div>
                      </td>
                      <td className="py-2 pr-4">
                        {s.agentName ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                            {s.agentName}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="py-2 pr-4 text-muted-foreground">
                        {s.authMethod || "-"}
                      </td>
                      <td className="py-2 pr-4 text-muted-foreground">
                        {s.tokenType || "-"}
                      </td>
                      <td className="py-2 pr-4 text-center text-foreground/80">
                        {s.requestCount ?? 0}
                      </td>
                      <td className="py-2 pr-4 text-xs text-muted-foreground">
                        {formatTimestamp(s.connectedAt)}
                      </td>
                      <td className="py-2 pr-4 text-xs text-muted-foreground">
                        {s.disconnectedAt ? formatTimestamp(s.disconnectedAt) : "-"}
                      </td>
                      <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">
                        {s.ipAddress || "-"}
                      </td>
                      <td className="py-2 pr-3">
                        {(() => {
                          const connected = (s.status || "DISCONNECTED") === "CONNECTED";
                          return (
                            <span
                              className={cn(
                                "inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-full border",
                                connected
                                  ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30"
                                  : "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30"
                              )}
                            >
                              <span
                                className={cn(
                                  "w-1.5 h-1.5 rounded-full",
                                  connected ? "bg-emerald-400" : "bg-red-400"
                                )}
                              />
                              {connected ? "Connected" : "Disconnected"}
                            </span>
                          );
                        })()}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={9} className="p-0 ag-surface border-b border-border">
                          <SessionDrillDown sessionId={sid} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && totalElements > 0 && (
        <div className="flex items-center justify-between px-4 py-3 rounded-lg border border-border bg-card">
          <span className="text-xs text-muted-foreground">
            Showing {from}–{to} of {totalElements.toLocaleString()}
          </span>
          <div className="flex items-center gap-2">
            <button
              disabled={currentPage === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="p-1 rounded-md border border-border text-muted-foreground hover:text-foreground/80 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <span className="text-xs text-muted-foreground">
              Page {currentPage + 1} / {Math.max(1, totalPages)}
            </span>
            <button
              disabled={currentPage >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
              className="p-1 rounded-md border border-border text-muted-foreground hover:text-foreground/80 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
            <select
              value={pageSize}
              onChange={(e) => setSize(Number(e.target.value))}
              className="ml-2 h-7 bg-card border border-border rounded-md px-2 text-xs text-foreground/80 outline-none"
            >
              {[10, 15, 25, 50].map((s) => (
                <option key={s} value={s}>
                  {s} / page
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

function NhiSessionsTab({ identityId }: { identityId: string }) {
  const { toast } = useToast();
  const [sessions, setSessions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    GatewayNhisApi
      .sessions(identityId)
      .then((data: any) => setSessions(data || []))
      .catch((e: any) => setError(apiError(e)))
      .finally(() => setLoading(false));
  }, [identityId]);

  if (loading)
    return (
      <div className="flex items-center gap-2 py-8 justify-center text-muted-foreground text-sm">
        <RefreshCw className="w-4 h-4 animate-spin" /> Loading sessions…
      </div>
    );
  if (error)
    return (
      <div className="text-sm text-red-600 dark:text-red-400 py-4 text-center">
        Failed to load sessions: {error}
      </div>
    );
  if (!sessions.length)
    return (
      <div className="text-sm text-muted-foreground py-8 text-center">
        No sessions found for this identity.
      </div>
    );

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-xs text-muted-foreground uppercase tracking-wider">
            <th className="text-left py-2 pr-4">Session ID</th>
            <th className="text-left py-2 pr-4">Agent</th>
            <th className="text-left py-2 pr-4">Auth Method</th>
            <th className="text-left py-2 pr-4">Token Type</th>
            <th className="text-center py-2 pr-4">Requests</th>
            <th className="text-left py-2 pr-4">Connected</th>
            <th className="text-left py-2 pr-4">Disconnected</th>
            <th className="text-left py-2 pr-4">IP</th>
            <th className="text-left py-2">Status</th>
          </tr>
        </thead>
        <tbody>
          {sessions.map((s: any) => {
            const sid = s.sessionId || "";
            const isExpanded = expandedId === sid;
            return (
              <React.Fragment key={sid}>
                <tr
                  className="border-b border-border hover:bg-primary/[0.025] cursor-pointer"
                  onClick={() => setExpandedId(isExpanded ? null : sid)}
                >
                  <td className="py-2 pr-4">
                    <div className="flex items-center gap-1">
                      {isExpanded ? (
                        <ChevronDown className="w-3 h-3 text-muted-foreground shrink-0" />
                      ) : (
                        <ChevronRight className="w-3 h-3 text-muted-foreground shrink-0" />
                      )}
                      <span className="font-mono text-xs text-muted-foreground">
                        {sid.substring(0, 20)}…
                      </span>
                      <button
                        className="text-muted-foreground/70 hover:text-foreground/80"
                        onClick={async (e) => {
                          e.stopPropagation();
                          await copyToClipboard(sid);
                          toast({ title: "Copied to clipboard" });
                        }}
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  </td>
                  <td className="py-2 pr-4">
                    {s.agentName ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                        {s.agentName}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </td>
                  <td className="py-2 pr-4 text-muted-foreground">
                    {s.authMethod || "-"}
                  </td>
                  <td className="py-2 pr-4 text-muted-foreground">
                    {s.tokenType || "-"}
                  </td>
                  <td className="py-2 pr-4 text-center text-foreground/80">
                    {s.requestCount ?? 0}
                  </td>
                  <td className="py-2 pr-4 text-xs text-muted-foreground">
                    {formatTimestamp(s.connectedAt)}
                  </td>
                  <td className="py-2 pr-4 text-xs text-muted-foreground">
                    {s.disconnectedAt ? formatTimestamp(s.disconnectedAt) : "-"}
                  </td>
                  <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">
                    {s.ipAddress || "-"}
                  </td>
                  <td className="py-2">
                    {(() => {
                      const connected = (s.status || "DISCONNECTED") === "CONNECTED";
                      return (
                        <span
                          className={cn(
                            "inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-full border",
                            connected
                              ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30"
                              : "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30"
                          )}
                        >
                          <span
                            className={cn(
                              "w-1.5 h-1.5 rounded-full",
                              connected ? "bg-emerald-400" : "bg-red-400"
                            )}
                          />
                          {connected ? "Connected" : "Disconnected"}
                        </span>
                      );
                    })()}
                  </td>
                </tr>
                {isExpanded && (
                  <tr>
                    <td
                      colSpan={9}
                      className="p-0 ag-surface border-b border-border"
                    >
                      <SessionDrillDown sessionId={sid} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}


function AnalyticsTab({
  identityId,
  identityType,
}: {
  identityId: string;
  identityType: IdentityType;
}) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const api =
      identityType === "human" ? GatewayHumanUsersApi : GatewayNhisApi;
    api
      .analytics(identityId, 24)
      .then((d: any) => setData(d))
      .catch((e: any) => setError(apiError(e)))
      .finally(() => setLoading(false));
  }, [identityId, identityType]);

  if (loading)
    return (
      <div className="flex items-center gap-2 py-8 justify-center text-muted-foreground text-sm">
        <RefreshCw className="w-4 h-4 animate-spin" /> Loading analytics…
      </div>
    );
  if (error)
    return (
      <div className="text-sm text-red-600 dark:text-red-400 py-4 text-center">{error}</div>
    );
  if (!data) return null;

  const s = data.summary || {};
  const timeline: any[] = data.timeline || [];
  const byAgent: any[] = data.byAgent || [];
  const byServer: any[] = data.byServer || [];
  const byTool: any[] = data.byTool || [];

  // backend timeline[] = { hour, requests } only
  const chartData = timeline.map((t: any) => ({
    hour: t.hour?.split("T")[1]?.substring(0, 5) || t.hour || "",
    requests: t.requests || 0,
  }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <GatewayStatCard
          icon={<Link2 className="w-5 h-5" />}
          label="Sessions (24h)"
          value={s.totalSessions ?? 0}
          colorClass="text-primary"
        />
        <GatewayStatCard
          icon={<Zap className="w-5 h-5" />}
          label="Tool Calls"
          value={s.totalToolCalls ?? 0}
          colorClass="text-amber-600 dark:text-amber-400"
        />
        <GatewayStatCard
          icon={<Activity className="w-5 h-5" />}
          label="Requests"
          value={s.totalRequests ?? 0}
          colorClass="text-emerald-600 dark:text-emerald-400"
        />
        <GatewayStatCard
          icon={<AlertTriangle className="w-5 h-5" />}
          label="Errors"
          value={s.totalErrors ?? 0}
          colorClass={(s.totalErrors ?? 0) > 0 ? "text-red-600 dark:text-red-400" : "text-muted-foreground"}
        />
      </div>

      {chartData.length > 0 && (
        <div className="ag-card p-4">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
            Hourly Activity (24h)
          </h4>
          <ResponsiveContainer width="100%" height={160}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="reqGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="errGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="hour" tick={{ fontSize: 10, fill: "#64748b" }} />
              <YAxis tick={{ fontSize: 10, fill: "#64748b" }} />
              <Tooltip
                contentStyle={{
                  background: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: 10,
                  boxShadow: "0 8px 24px -6px rgb(15 23 42 / 0.18)",
                  fontSize: 12,
                }}
              />
              <Area
                type="monotone"
                dataKey="requests"
                stroke="#06b6d4"
                fill="url(#reqGrad)"
                strokeWidth={1.5}
                name="Requests"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {byAgent.length > 0 && (
          <div className="ag-card p-4">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
              By Agent
            </h4>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-muted-foreground border-b border-border">
                  <th className="text-left pb-2">Agent</th>
                  <th className="text-right pb-2">Sessions</th>
                  <th className="text-right pb-2">Tool Calls</th>
                  <th className="text-right pb-2">Requests</th>
                </tr>
              </thead>
              <tbody>
                {byAgent.map((a: any, i: number) => (
                  <tr key={i} className="border-b border-border">
                    <td className="py-1.5 font-medium text-foreground/80">
                      {a.agentName || "unknown"}
                    </td>
                    <td className="py-1.5 text-right text-muted-foreground">
                      {a.sessions ?? 0}
                    </td>
                    <td className="py-1.5 text-right text-muted-foreground">
                      {a.toolCalls ?? 0}
                    </td>
                    <td className="py-1.5 text-right text-muted-foreground">
                      {a.totalRequests ?? 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {byServer.length > 0 && (
          <div className="ag-card p-4">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
              By Server
            </h4>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-muted-foreground border-b border-border">
                  <th className="text-left pb-2">Server</th>
                  <th className="text-right pb-2">Requests</th>
                  <th className="text-right pb-2">Errors</th>
                </tr>
              </thead>
              <tbody>
                {byServer.map((s: any, i: number) => (
                  <tr key={i} className="border-b border-border">
                    <td className="py-1.5 font-medium text-foreground/80">
                      {s.serverName || "unknown"}
                    </td>
                    <td className="py-1.5 text-right text-muted-foreground">
                      {s.requests ?? 0}
                    </td>
                    <td
                      className={cn(
                        "py-1.5 text-right",
                        (s.errors ?? 0) > 0 ? "text-red-600 dark:text-red-400" : "text-muted-foreground"
                      )}
                    >
                      {s.errors ?? 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {byTool.length > 0 && (
        <div className="ag-card p-4">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
            By Tool
          </h4>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-muted-foreground border-b border-border">
                <th className="text-left pb-2">Tool</th>
                <th className="text-left pb-2">Server</th>
                <th className="text-right pb-2">Calls</th>
                <th className="text-right pb-2">Avg Duration</th>
              </tr>
            </thead>
            <tbody>
              {byTool.map((t: any, i: number) => (
                <tr key={i} className="border-b border-border">
                  <td className="py-1.5 font-mono text-foreground/80">
                    {t.toolName || "unknown"}
                  </td>
                  <td className="py-1.5 text-muted-foreground">
                    {t.serverName || "—"}
                  </td>
                  <td className="py-1.5 text-right text-muted-foreground">
                    {t.calls ?? 0}
                  </td>
                  <td className="py-1.5 text-right text-muted-foreground">
                    {formatDuration(t.avgDurationMs)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function LineageTab({
  identityId,
  identityType,
}: {
  identityId: string;
  identityType: IdentityType;
}) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const api =
      identityType === "human" ? GatewayHumanUsersApi : GatewayNhisApi;
    api
      .lineage(identityId)
      .then((d: any) => setData(d))
      .catch((e: any) => setError(apiError(e)))
      .finally(() => setLoading(false));
  }, [identityId, identityType]);

  if (loading)
    return (
      <div className="flex items-center gap-2 py-8 justify-center text-muted-foreground text-sm">
        <RefreshCw className="w-4 h-4 animate-spin" /> Loading lineage…
      </div>
    );
  if (error)
    return <div className="text-sm text-red-600 dark:text-red-400 py-4 text-center">{error}</div>;
  if (!data) return null;

  const agentsUsed: any[] = data.agentsUsed || [];
  const recentActions: any[] = data.recentActions || [];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-3">
        <GatewayStatCard
          icon={<Link2 className="w-5 h-5" />}
          label="Total Sessions"
          value={data.totalSessions ?? 0}
          colorClass="text-primary"
        />
        <GatewayStatCard
          icon={<Zap className="w-5 h-5" />}
          label="Total Tool Calls"
          value={data.totalToolCalls ?? 0}
          colorClass="text-amber-600 dark:text-amber-400"
        />
        <GatewayStatCard
          icon={<Bot className="w-5 h-5" />}
          label="Agents Used"
          value={agentsUsed.length}
          colorClass="text-violet-600 dark:text-violet-400"
        />
      </div>

      <div className="ag-card p-4">
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
          Agents Used
        </h4>
        {agentsUsed.length === 0 ? (
          <p className="text-xs text-muted-foreground">No agents used yet.</p>
        ) : (
          <div className="space-y-3">
            {agentsUsed.map((a: any, i: number) => (
              <div
                key={i}
                className="rounded-lg border border-border ag-surface p-3 portal-card-hover"
              >
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="font-semibold text-foreground/80 text-sm">
                      {a.agentName || "unknown"}
                    </span>
                    {a.authClientId && (
                      <span className="ml-2 text-xs text-muted-foreground font-mono">
                        client: {a.authClientId}
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {a.sessionsCount ?? 0} sessions ·{" "}
                    {a.toolCallsCount ?? 0} tool calls · last{" "}
                    {formatTimestamp(a.lastUsedAt)}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {(a.toolsUsed || []).map((t: string) => (
                    <span
                      key={t}
                      className="font-mono text-xs px-1.5 py-0.5 rounded bg-sky-500/10 text-sky-600 dark:text-sky-400 border border-sky-500/20"
                    >
                      {t}
                    </span>
                  ))}
                  {(!a.toolsUsed || !a.toolsUsed.length) && (
                    <span className="text-xs text-muted-foreground">
                      No tools used
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {recentActions.length > 0 && (
        <div className="ag-card p-4 overflow-x-auto">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
            Recent Actions ({recentActions.length})
          </h4>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-muted-foreground border-b border-border">
                <th className="text-left pb-2 pr-4">Time</th>
                <th className="text-left pb-2 pr-4">Agent</th>
                <th className="text-left pb-2 pr-4">Action</th>
                <th className="text-left pb-2 pr-4">Tool</th>
                <th className="text-left pb-2 pr-4">Server</th>
                <th className="text-left pb-2 pr-4">Status</th>
                <th className="text-left pb-2">PDP</th>
              </tr>
            </thead>
            <tbody>
              {recentActions.slice(0, 50).map((a: any, i: number) => (
                <tr key={i} className="border-b border-border">
                  <td className="py-1.5 pr-4 text-muted-foreground">
                    {formatTimestamp(a.timestamp)}
                  </td>
                  <td className="py-1.5 pr-4 text-foreground/80">
                    {a.agentName || "-"}
                  </td>
                  <td className="py-1.5 pr-4">
                    <span className="px-1.5 py-0.5 rounded text-xs bg-sky-500/10 text-sky-600 dark:text-sky-400 border border-sky-500/20">
                      {a.action || "-"}
                    </span>
                  </td>
                  <td className="py-1.5 pr-4 font-mono text-muted-foreground">
                    {a.toolName || "-"}
                  </td>
                  <td className="py-1.5 pr-4 text-muted-foreground">
                    {a.serverName || "-"}
                  </td>
                  <td className="py-1.5 pr-4">
                    {a.status ? (
                      <GatewayStatusBadge status={a.status} />
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="py-1.5">
                    {a.pdpDecision ? (
                      <span
                        className={cn(
                          "px-1.5 py-0.5 rounded text-xs border",
                          a.pdpDecision === "ALLOW"
                            ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20"
                            : "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20"
                        )}
                      >
                        {a.pdpDecision}
                      </span>
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function RiskTab({
  identityId,
  identityType,
}: {
  identityId: string;
  identityType: IdentityType;
}) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const api =
      identityType === "human" ? GatewayHumanUsersApi : GatewayNhisApi;
    api
      .riskAssessment(identityId, 24)
      .then((d: any) => setData(d))
      .catch((e: any) => setError(apiError(e)))
      .finally(() => setLoading(false));
  }, [identityId, identityType]);

  if (loading)
    return (
      <div className="flex items-center gap-2 py-8 justify-center text-muted-foreground text-sm">
        <RefreshCw className="w-4 h-4 animate-spin" /> Loading risk assessment…
      </div>
    );
  if (error)
    return <div className="text-sm text-red-600 dark:text-red-400 py-4 text-center">{error}</div>;
  if (!data) return null;

  const score: number = data.riskScore ?? 0;
  const level: string = data.riskLevel || "LOW";
  const signals: any[] = data.signals || [];

  const levelColor =
    level === "CRITICAL"
      ? "#ef4444"
      : level === "HIGH"
      ? "#f97316"
      : level === "MEDIUM"
      ? "#f59e0b"
      : "#22c55e";

  const circumference = 2 * Math.PI * 50;
  const dashArray = `${(score / 100) * circumference} ${circumference - (score / 100) * circumference}`;

  const severityClasses: Record<string, string> = {
    CRITICAL: "text-red-600 dark:text-red-400",
    HIGH: "text-orange-600 dark:text-orange-400",
    ELEVATED: "text-amber-600 dark:text-amber-400",
    LOW: "text-yellow-600 dark:text-yellow-400",
    NORMAL: "text-emerald-600 dark:text-emerald-400",
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center gap-3 py-4">
        <div className="relative w-32 h-32">
          <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
            <circle
              cx="60"
              cy="60"
              r="50"
              fill="none"
              stroke="hsl(var(--primary) / 0.1)"
              strokeWidth="10"
            />
            <circle
              cx="60"
              cy="60"
              r="50"
              fill="none"
              stroke={levelColor}
              strokeWidth="10"
              strokeDasharray={dashArray}
              strokeLinecap="round"
              className="transition-all duration-700"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span
              className="text-3xl font-bold"
              style={{ color: levelColor }}
            >
              {score}
            </span>
            <span className="text-xs text-muted-foreground">/ 100</span>
          </div>
        </div>
        <span
          className="px-3 py-1 rounded-full text-sm font-semibold text-white"
          style={{ background: levelColor }}
        >
          {level}
        </span>
        <span className="text-xs text-muted-foreground">
          Assessed: {formatTimestamp(data.assessedAt)} · Window:{" "}
          {data.windowHours ?? 24}h
        </span>
      </div>

      {signals.length > 0 && (
        <div className="ag-card p-4 overflow-x-auto">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
            Risk Signals
          </h4>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-muted-foreground border-b border-border">
                <th className="text-left pb-2 pr-4">Signal</th>
                <th className="text-right pb-2 pr-4">Value</th>
                <th className="text-right pb-2 pr-4">Baseline</th>
                <th className="text-left pb-2 pr-4">Severity</th>
                <th className="text-left pb-2">Description</th>
              </tr>
            </thead>
            <tbody>
              {signals.map((s: any, i: number) => (
                <tr key={i} className="border-b border-border">
                  <td className="py-1.5 pr-4 font-medium text-foreground/80">
                    {s.signal || "-"}
                  </td>
                  <td className="py-1.5 pr-4 text-right text-muted-foreground">
                    {s.value ?? "-"}
                  </td>
                  <td className="py-1.5 pr-4 text-right text-muted-foreground">
                    {s.baseline ?? "-"}
                  </td>
                  <td
                    className={cn(
                      "py-1.5 pr-4 font-semibold",
                      severityClasses[s.severity] || "text-muted-foreground"
                    )}
                  >
                    {s.severity || "-"}
                  </td>
                  <td className="py-1.5 text-muted-foreground">
                    {s.description || "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function HumanOverview({ identity: h }: { identity: any }) {
  const { toast } = useToast();
  const shortId = (h.id || "").substring(0, 13);

  const insights: string[] = [];
  const now = Date.now();
  const firstSeen = h.firstSeenAt ? new Date(h.firstSeenAt).getTime() : null;
  if (firstSeen && now - firstSeen < 86400000)
    insights.push("New user — first seen less than 24 hours ago.");
  if ((h.totalSessions || 0) > 10)
    insights.push(`High session count: ${h.totalSessions} sessions recorded.`);
  if (h.blockedReason) insights.push(`Previously blocked: ${h.blockedReason}`);
  if (!insights.length)
    insights.push("No unusual activity detected in the last 24 hours.");

  const FieldRow = ({
    label,
    value,
    mono,
  }: {
    label: string;
    value: React.ReactNode;
    mono?: boolean;
  }) => (
    <div className="flex justify-between items-start py-1.5 border-b border-border last:border-0">
      <span className="text-xs text-muted-foreground shrink-0 w-36">{label}</span>
      <span className={cn("text-sm text-foreground/80 text-right", mono && "font-mono text-xs")}>
        {value}
      </span>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="ag-card p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1.5 h-1.5 rounded-full bg-primary" />
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Identity Details
            </span>
            <span className="ml-auto text-xs text-muted-foreground/70 font-mono">
              ID: {shortId}
            </span>
          </div>
          <FieldRow label="Username" value={h.preferredUsername || "-"} />
          <FieldRow label="Full Name" value={h.fullName || "-"} />
          <FieldRow
            label="Email"
            value={
              <span>
                {h.email || "-"}
                {h.emailVerified && (
                  <span className="ml-1 text-xs text-emerald-600 dark:text-emerald-400">✓ VERIFIED</span>
                )}
              </span>
            }
          />
          <FieldRow label="Given Name" value={h.givenName || "-"} />
          <FieldRow label="Family Name" value={h.familyName || "-"} />
          <FieldRow label="IdP Subject" value={h.idpSubject || "-"} mono />
          <FieldRow label="IdP Issuer" value={h.idpIssuer || "-"} mono />
        </div>

        <div className="ag-card p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1.5 h-1.5 rounded-full bg-primary" />
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Session Activity
            </span>
          </div>
          <FieldRow label="First Seen" value={formatTimestamp(h.firstSeenAt)} />
          <FieldRow label="Last Seen" value={formatTimestamp(h.lastSeenAt)} />
          <FieldRow label="Last IP" value={h.lastIpAddress || "-"} mono />
          <FieldRow
            label="Total Sessions"
            value={
              <span className="text-lg font-bold text-foreground">
                {h.totalSessions ?? 0}
              </span>
            }
          />
          <FieldRow
            label="Total Requests"
            value={
              <span className="text-lg font-bold text-foreground">
                {h.totalRequests ?? 0}
              </span>
            }
          />
          {h.blockedReason && (
            <FieldRow
              label="Blocked Reason"
              value={
                <span className="text-red-600 dark:text-red-400">{h.blockedReason}</span>
              }
            />
          )}
          {h.blockedAt && (
            <FieldRow label="Blocked At" value={formatTimestamp(h.blockedAt)} />
          )}
        </div>
      </div>

      <div className="flex items-start gap-3 rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
        <Shield className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
        <div>
          <div className="text-xs font-semibold text-amber-600 dark:text-amber-400 mb-1">
            Security Insight
          </div>
          <div className="text-sm text-foreground/80">{insights[0]}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="ag-card p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Roles & Permissions
            </span>
          </div>
          <div className="mb-3">
            <div className="text-xs text-muted-foreground mb-1">Realm Roles</div>
            <RoleBadges roles={h.realmRoles || []} />
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">Client Roles</div>
            <RoleBadges roles={h.clientRoles || []} />
          </div>
        </div>

        <div className="ag-card p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1.5 h-1.5 rounded-full bg-violet-400" />
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Custom Claims
            </span>
          </div>
          {h.customClaims && Object.keys(h.customClaims).length > 0 ? (
            <pre className="text-xs text-foreground/80 max-h-48 overflow-auto whitespace-pre-wrap break-all">
              {JSON.stringify(h.customClaims, null, 2)}
            </pre>
          ) : (
            <span className="text-xs text-muted-foreground">
              No custom claims (ws_gateway_* prefixed)
            </span>
          )}
        </div>
      </div>

      {h.lastJwtClaims && (
        <div className="ag-card p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-indigo-400" />
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Last JWT Claims (Raw)
              </span>
            </div>
            <button
              className="text-xs text-muted-foreground hover:text-foreground/80 flex items-center gap-1"
              onClick={async () => {
                await copyToClipboard(JSON.stringify(h.lastJwtClaims, null, 2));
                toast({ title: "Copied to clipboard" });
              }}
            >
              <Copy className="w-3 h-3" /> Copy
            </button>
          </div>
          <ColoredJson data={h.lastJwtClaims} />
        </div>
      )}
    </div>
  );
}


function NhiOverview({ identity: n }: { identity: any }) {
  const { toast } = useToast();
  const shortId = (n.id || "").substring(0, 13);
  const isActive = n.status === "ACTIVE";

  const insights: string[] = [];
  const now = Date.now();
  const firstSeen = n.firstSeenAt ? new Date(n.firstSeenAt).getTime() : null;
  if (firstSeen && now - firstSeen < 86400000)
    insights.push("New NHI — first seen less than 24 hours ago.");
  if ((n.totalSessions || 0) > 50)
    insights.push(`High session count: ${n.totalSessions} sessions recorded.`);
  if (n.blockedReason) insights.push(`Previously blocked: ${n.blockedReason}`);
  if (!insights.length)
    insights.push("No unusual activity detected in the last 24 hours.");

  const FieldRow = ({
    label,
    value,
    mono,
  }: {
    label: string;
    value: React.ReactNode;
    mono?: boolean;
  }) => (
    <div className="flex justify-between items-start py-1.5 border-b border-border last:border-0">
      <span className="text-xs text-muted-foreground shrink-0 w-36">{label}</span>
      <span className={cn("text-sm text-foreground/80 text-right break-all max-w-xs", mono && "font-mono text-xs")}>
        {value}
      </span>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="ag-card p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              NHI Identity Details
            </span>
            <span className="ml-auto text-xs text-muted-foreground/70 font-mono">
              ID: {shortId}
            </span>
          </div>
          <FieldRow label="Service Name" value={n.serviceName || "-"} />
          <FieldRow label="Client ID" value={n.clientId || "-"} mono />
          <FieldRow label="IdP Subject" value={n.idpSubject || "-"} mono />
          <FieldRow label="IdP Issuer" value={n.idpIssuer || "-"} mono />
          <FieldRow label="Description" value={n.description || "-"} />
        </div>

        <div className="ag-card p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1.5 h-1.5 rounded-full bg-primary" />
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Session Activity
            </span>
          </div>
          <FieldRow label="First Seen" value={formatTimestamp(n.firstSeenAt)} />
          <FieldRow label="Last Seen" value={formatTimestamp(n.lastSeenAt)} />
          <FieldRow label="Last IP" value={n.lastIpAddress || "-"} mono />
          <FieldRow
            label="Total Sessions"
            value={
              <span className="text-lg font-bold text-foreground">
                {n.totalSessions ?? 0}
              </span>
            }
          />
          <FieldRow
            label="Total Requests"
            value={
              <span className="text-lg font-bold text-foreground">
                {n.totalRequests ?? 0}
              </span>
            }
          />
          <FieldRow
            label="Status"
            value={
              <span
                className={cn(
                  "inline-flex items-center gap-1 text-xs font-semibold",
                  isActive ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"
                )}
              >
                <span
                  className={cn(
                    "w-1.5 h-1.5 rounded-full",
                    isActive ? "bg-emerald-400" : "bg-red-400"
                  )}
                />
                {isActive ? "ACTIVE" : n.status}
              </span>
            }
          />
          {n.blockedReason && (
            <FieldRow
              label="Blocked Reason"
              value={<span className="text-red-600 dark:text-red-400">{n.blockedReason}</span>}
            />
          )}
          {n.blockedAt && (
            <FieldRow label="Blocked At" value={formatTimestamp(n.blockedAt)} />
          )}
        </div>
      </div>

      <div className="flex items-start gap-3 rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
        <Shield className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
        <div>
          <div className="text-xs font-semibold text-amber-600 dark:text-amber-400 mb-1">
            Security Insight
          </div>
          <div className="text-sm text-foreground/80">{insights[0]}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="ag-card p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Realm Roles
            </span>
          </div>
          <RoleBadges roles={n.realmRoles || []} />
          <div className="flex items-center gap-2 mt-4 mb-3">
            <div className="w-1.5 h-1.5 rounded-full bg-violet-400" />
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Client Roles
            </span>
          </div>
          <RoleBadges roles={n.clientRoles || []} />
        </div>

        <div className="ag-card p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1.5 h-1.5 rounded-full bg-violet-400" />
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Custom Claims
            </span>
          </div>
          {n.customClaims && Object.keys(n.customClaims).length > 0 ? (
            <pre className="text-xs text-foreground/80 max-h-48 overflow-auto whitespace-pre-wrap break-all">
              {JSON.stringify(n.customClaims, null, 2)}
            </pre>
          ) : (
            <span className="text-xs text-muted-foreground">No custom claims</span>
          )}
        </div>
      </div>

      {n.lastJwtClaims && (
        <div className="ag-card p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-indigo-400" />
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Last JWT Claims (Raw)
              </span>
            </div>
            <button
              className="text-xs text-muted-foreground hover:text-foreground/80 flex items-center gap-1"
              onClick={async () => {
                await copyToClipboard(JSON.stringify(n.lastJwtClaims, null, 2));
                toast({ title: "Copied to clipboard" });
              }}
            >
              <Copy className="w-3 h-3" /> Copy
            </button>
          </div>
          <ColoredJson data={n.lastJwtClaims} />
        </div>
      )}
    </div>
  );
}


function IdentityCard({
  identity,
  type,
  sensitivity,
  onClick,
}: {
  identity: any;
  type: IdentityType;
  sensitivity?: string;
  onClick: () => void;
}) {
  const isHuman = type === "human";
  const displayName = isHuman
    ? identity.fullName || identity.preferredUsername || "Unknown"
    : identity.serviceName || identity.clientId || "Unknown";
  const subName = isHuman
    ? `@${identity.preferredUsername || ""}`
    : identity.clientId || "-";
  const detail = isHuman
    ? identity.email || "-"
    : identity.idpIssuer || "-";
  const allRoles = [...(identity.realmRoles || []), ...(identity.clientRoles || [])];

  return (
    <div
      className="ag-entity ag-clickable"
      onClick={onClick}
    >
      <div className="ag-entity-head">
        <div className="ag-entity-identity">
          <span className="ag-entity-avatar" data-tone={isHuman ? "violet" : "amber"} aria-hidden="true">
            {isHuman ? <User /> : <Bot />}
          </span>
          <div className="min-w-0">
            <h3 className="ag-entity-name" title={displayName}>{displayName}</h3>
            <p className="ag-hint truncate" title={`${subName} · ${detail}`}>{subName} · {detail}</p>
          </div>
        </div>
        <IdentityStatusBadge status={identity.status || "UNKNOWN"} />
      </div>

      <div className="ag-entity-body">
        <div className="ag-server-metrics ag-metrics-3">
          <div className="ag-metric">
            <span className="ag-metric-label">Sessions</span>
            <span className="ag-metric-value text-lg">{identity.totalSessions ?? 0}</span>
          </div>
          <div className="ag-metric">
            <span className="ag-metric-label">Requests</span>
            <span className="ag-metric-value text-lg">{identity.totalRequests ?? 0}</span>
          </div>
          <div className="ag-metric">
            <span className="ag-metric-label">Roles</span>
            <span className="ag-metric-value text-lg">{allRoles.length}</span>
          </div>
        </div>

        {allRoles.length > 0 && <RoleBadges roles={allRoles} />}

        {sensitivity && (
          <div className="ag-kv">
            <span>Sensitivity</span>
            <GatewaySensitivityBadge value={sensitivity} />
          </div>
        )}

        <div className="ag-server-foot">
          <span>
            <Clock aria-hidden="true" />
            {formatTimestamp(identity.lastSeenAt)}
          </span>
          {identity.lastIpAddress && (
            <span className="ag-mono">
              <Globe aria-hidden="true" />
              {identity.lastIpAddress}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}


function ActiveNowContent() {
  const [humanData, setHumanData] = useState<any>({ activeHumans: [], totalActiveHumans: 0, totalActiveSessions: 0 });
  const [nhiData, setNhiData] = useState<any>({ activeNhis: [], totalActiveNhis: 0, totalActiveSessions: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const [hd, nd] = await Promise.all([
      GatewayHumanUsersApi.activeNow().catch(() => ({ activeHumans: [], totalActiveHumans: 0, totalActiveSessions: 0 })),
      GatewayNhisApi.activeNow().catch(() => ({ activeNhis: [], totalActiveNhis: 0, totalActiveSessions: 0 })),
    ]);
    setHumanData(hd || {});
    setNhiData(nd || {});
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  if (loading)
    return (
      <div className="flex items-center gap-2 py-8 justify-center text-muted-foreground text-sm">
        <RefreshCw className="w-4 h-4 animate-spin" /> Loading active identities…
      </div>
    );

  const activeHumans: any[] = humanData.activeHumans || [];
  const activeNhis: any[] = nhiData.activeNhis || [];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-3">
        <GatewayStatCard
          icon={<Users className="w-5 h-5" />}
          label="Active Humans"
          value={humanData.totalActiveHumans ?? 0}
          colorClass="text-violet-600 dark:text-violet-400"
        />
        <GatewayStatCard
          icon={<Bot className="w-5 h-5" />}
          label="Active NHIs"
          value={nhiData.totalActiveNhis ?? 0}
          colorClass="text-amber-600 dark:text-amber-400"
        />
        <GatewayStatCard
          icon={<Zap className="w-5 h-5" />}
          label="Total Active Sessions"
          value={(humanData.totalActiveSessions ?? 0) + (nhiData.totalActiveSessions ?? 0)}
          colorClass="text-emerald-600 dark:text-emerald-400"
        />
      </div>

      <div>
        <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground/80 mb-3">
          <Users className="w-4 h-4 text-violet-600 dark:text-violet-400" />
          Active Humans ({activeHumans.length})
        </h3>
        {activeHumans.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4 text-center">
            No humans are actively connected right now.
          </div>
        ) : (
          <div className="space-y-3">
            {activeHumans.map((h: any) => (
              <div
                key={h.id}
                className="ag-card p-4"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shrink-0">
                    <Users className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-foreground/80 text-sm">
                      {h.fullName || h.preferredUsername || "unknown"}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      @{h.preferredUsername || ""} · {h.email || ""}
                    </div>
                  </div>
                  <div className="ml-auto">
                    <RoleBadges roles={h.realmRoles || []} />
                  </div>
                </div>
                <div className="pl-11 space-y-1">
                  {(h.activeSessions || []).map((s: any, i: number) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 py-1.5 border-b border-border text-xs last:border-0"
                    >
                      <span className="px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 text-xs">
                        CONNECTED
                      </span>
                      <span className="font-medium text-foreground/80">
                        {s.agentName || "unknown"}
                      </span>
                      <span className="text-muted-foreground">
                        idle: {s.idleDurationMs != null ? formatDuration(s.idleDurationMs) : "-"}
                      </span>
                      <span className="text-muted-foreground">
                        reqs: {s.requestCount ?? 0}
                      </span>
                      <span className="text-muted-foreground">
                        since: {formatTimestamp(s.connectedSince)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground/80 mb-3">
          <Bot className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          Active NHIs ({activeNhis.length})
        </h3>
        {activeNhis.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4 text-center">
            No NHIs are actively connected right now.
          </div>
        ) : (
          <div className="space-y-3">
            {activeNhis.map((n: any) => (
              <div
                key={n.id}
                className="ag-card p-4"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-red-500 flex items-center justify-center shrink-0">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-foreground/80 text-sm">
                      {n.serviceName || "unknown"}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      client: {n.clientId || ""} · issuer:{" "}
                      {n.idpIssuer || "-"}
                    </div>
                  </div>
                  <div className="ml-auto">
                    <RoleBadges roles={n.realmRoles || []} />
                  </div>
                </div>
                <div className="pl-11 space-y-1">
                  {(n.activeSessions || []).map((s: any, i: number) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 py-1.5 border-b border-border text-xs last:border-0"
                    >
                      <span className="px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 text-xs">
                        CONNECTED
                      </span>
                      <span className="font-medium text-foreground/80">
                        {s.agentName || "unknown"}
                      </span>
                      <span className="text-muted-foreground">
                        idle: {s.idleDurationMs != null ? formatDuration(s.idleDurationMs) : "-"}
                      </span>
                      <span className="text-muted-foreground">
                        reqs: {s.requestCount ?? 0}
                      </span>
                      <span className="text-muted-foreground">
                        since: {formatTimestamp(s.connectedSince)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


interface DetailViewProps {
  identity: any;
  identityType: IdentityType;
  onBack: () => void;
  onStatusChange: () => void;
}

// A clickable item in the header "Actions" popover: colored title on top, small detail beneath.
function ActionMenuItem({
  icon,
  color,
  title,
  desc,
  onClick,
  disabled,
  busy,
}: {
  icon: React.ReactNode;
  color: string;
  title: string;
  desc: string;
  onClick: () => void;
  disabled?: boolean;
  busy?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="w-full text-left rounded-lg px-3 py-2.5 hover:bg-primary/[0.025] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <span className={cn("flex items-center gap-2 text-sm font-medium", color)}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : icon}
        {title}
      </span>
      <span className="block text-xs text-muted-foreground leading-relaxed mt-0.5 pl-6">{desc}</span>
    </button>
  );
}

function DetailView({
  identity,
  identityType,
  onBack,
  onStatusChange,
}: DetailViewProps) {
  const [activeTab, setActiveTab] = useState<DetailTab>("overview");
  const { toast } = useToast();
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [blockReason, setBlockReason] = useState("");
  const [approving, setApproving] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const [unblocking, setUnblocking] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);

  const isHuman = identityType === "human";
  const isPending = identity.status === "PENDING";
  const isActive = identity.status === "ACTIVE";

  const displayName = isHuman
    ? identity.fullName || identity.preferredUsername || "Unknown"
    : identity.serviceName || identity.clientId || "Unknown";
  const subLine = isHuman
    ? `@${identity.preferredUsername || ""} · ${identity.email || "-"}`
    : `client: ${identity.clientId || "-"} · issuer: ${identity.idpIssuer || "-"}`;

  const api = isHuman ? GatewayHumanUsersApi : GatewayNhisApi;

  async function handleApprove() {
    setApproving(true);
    try {
      const res = await api.approve(identity.id);
      toast({ description: (res as any).message || "Identity approved", variant: "default" });
      onStatusChange();
    } catch (e: any) {
      toast({ description: `Failed to approve: ${apiError(e)}`, variant: "destructive" });
    } finally {
      setApproving(false);
    }
  }

  function openBlockDialog() {
    setBlockReason("");
    setBlockDialogOpen(true);
  }

  async function confirmBlock() {
    if (!blockReason.trim()) return;
    setBlocking(true);
    try {
      const res = await api.block(identity.id, { reason: blockReason });
      const parts = [(res as any).message || "Identity blocked"];
      if ((res as any).sessionsTerminated > 0)
        parts.push(`${(res as any).sessionsTerminated} session(s) terminated`);
      if ((res as any).affectedAgents > 0)
        parts.push(`${(res as any).affectedAgents} agent(s) affected`);
      toast({ description: parts.join(" · ") });
      setBlockDialogOpen(false);
      onStatusChange();
    } catch (e: any) {
      toast({ description: `Failed to block: ${apiError(e)}`, variant: "destructive" });
    } finally {
      setBlocking(false);
    }
  }

  async function handleUnblock() {
    setUnblocking(true);
    try {
      await api.unblock(identity.id);
      toast({ description: "Identity unblocked" });
      onStatusChange();
    } catch (e: any) {
      toast({ description: `Failed to unblock: ${apiError(e)}`, variant: "destructive" });
    } finally {
      setUnblocking(false);
    }
  }

  const detailTabs: { id: DetailTab; label: string; icon: React.ReactNode }[] = [
    { id: "overview", label: "Overview", icon: <Shield className="w-3.5 h-3.5" /> },
    { id: "analytics", label: "Analytics", icon: <BarChart2 className="w-3.5 h-3.5" /> },
    { id: "sessions", label: "Sessions", icon: <Link2 className="w-3.5 h-3.5" /> },
    { id: "lineage", label: "Lineage", icon: <GitBranch className="w-3.5 h-3.5" /> },
    { id: "risk", label: "Risk", icon: <AlertTriangle className="w-3.5 h-3.5" /> },
  ];

  const statusTone = isPending ? "amber" : isActive ? "success" : "danger";

  return (
    <>
      <div className="ag-card ag-detail-header">
        <div className="ag-detail-identity">
          <button
            type="button"
            onClick={onBack}
            className="ag-back-button"
            title="Back to Identities"
            aria-label="Back to Identities"
          >
            <ArrowLeft />
          </button>
          <span className="ag-detail-avatar relative" data-tone={isHuman ? "violet" : "amber"} aria-hidden="true">
            {isHuman ? <User /> : <Bot />}
            <span className="ag-server-dot" data-tone={statusTone} />
          </span>
          <div className="min-w-0">
            <h1 className="ag-detail-title">
              <span className="truncate">{displayName}</span>
              <IdentityStatusBadge status={identity.status} />
            </h1>
            <div className="ag-detail-meta">
              <span className="truncate">{subLine}</span>
            </div>
          </div>
        </div>

        <Popover open={actionsOpen} onOpenChange={setActionsOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="ag-button no-default-hover-elevate shrink-0"
            >
              <Shield />
              Actions
              <ChevronDown />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" sideOffset={8} className="w-80 p-1.5 bg-card border-border rounded-xl shadow-[0_12px_32px_-8px_rgb(15_23_42/0.18)]">
            {isPending && (
              <ActionMenuItem
                icon={<CheckCircle className="w-4 h-4" />}
                color="text-emerald-600 dark:text-emerald-400"
                title="Approve"
                desc="Allow this identity to authenticate and obtain tokens through the gateway."
                disabled={approving}
                busy={approving}
                onClick={() => { setActionsOpen(false); handleApprove(); }}
              />
            )}
            {(isPending || isActive) && (
              <ActionMenuItem
                icon={<Ban className="w-4 h-4" />}
                color="text-red-600 dark:text-red-400"
                title="Block"
                desc="Immediately revoke access and terminate active sessions. A reason is required."
                onClick={() => { setActionsOpen(false); openBlockDialog(); }}
              />
            )}
            {!isActive && !isPending && (
              <ActionMenuItem
                icon={<CheckCircle className="w-4 h-4" />}
                color="text-emerald-600 dark:text-emerald-400"
                title="Unblock"
                desc="Restore access for this previously blocked identity."
                disabled={unblocking}
                busy={unblocking}
                onClick={() => { setActionsOpen(false); handleUnblock(); }}
              />
            )}
          </PopoverContent>
        </Popover>
      </div>

      <div className="ag-card ag-toolbar ag-nav">
        <div className="cea-tabs ag-tabs" role="tablist" aria-label="Identity details">
          {detailTabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={activeTab === t.id}
              className={cn("cea-tab", activeTab === t.id && "active")}
              onClick={() => setActiveTab(t.id)}
            >
              {t.icon}
              {t.label}
            </button>
          ))}
        </div>
        <span className="ag-summary">
          {isHuman ? <User aria-hidden="true" /> : <Bot aria-hidden="true" />}
          <span>{isHuman ? "Human identity" : "Non-human identity"}</span>
        </span>
      </div>

      <div className="ag-card ag-content">
        <div className="ag-scroll">
          {activeTab === "overview" &&
            (isHuman ? (
              <HumanOverview identity={identity} />
            ) : (
              <NhiOverview identity={identity} />
            ))}
          {activeTab === "analytics" && (
            <AnalyticsTab
              identityId={identity.id}
              identityType={identityType}
            />
          )}
          {activeTab === "sessions" && (
            <SessionsTab
              identityId={identity.id}
              identityType={identityType}
            />
          )}
          {activeTab === "lineage" && (
            <LineageTab
              identityId={identity.id}
              identityType={identityType}
            />
          )}
          {activeTab === "risk" && (
            <RiskTab identityId={identity.id} identityType={identityType} />
          )}
        </div>
      </div>

      <Dialog open={blockDialogOpen} onOpenChange={setBlockDialogOpen}>
        <DialogContent className="management-dialog ag-dialog sm:max-w-md">
          <DialogHeader className="management-dialog-header">
            <div className="management-dialog-heading">
              <span className="management-dialog-icon" data-tone="danger" aria-hidden="true">
                <Ban />
              </span>
              <div>
                <DialogTitle>Block Identity</DialogTitle>
                <DialogDescription>Provide a reason for blocking this identity.</DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="management-dialog-body">
            <textarea
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
              placeholder="Reason for blocking..."
              rows={3}
              className="ag-input"
            />
          </div>
          <DialogFooter className="management-dialog-footer">
            <Button variant="outline" onClick={() => setBlockDialogOpen(false)} disabled={blocking}>Cancel</Button>
            <Button onClick={confirmBlock} disabled={!blockReason.trim() || blocking} className="ag-danger-button">
              {blocking ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Ban className="w-3.5 h-3.5 mr-1.5" />}
              Block
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


export default function GatewayIdentitiesPage() {
  const { toast } = useToast();

  const [listTab, setListTab] = useState<ListTab>("humans");
  const [humans, setHumans] = useState<any[]>([]);
  const [nhis, setNhis] = useState<any[]>([]);
  const [humanSummary, setHumanSummary] = useState<HumanSummary>({});
  const [nhiSummary, setNhiSummary] = useState<NhiSummary>({});
  // Peak egress-sensitivity per identity (post-processor insights; empty if disabled)
  const [sensHumans, setSensHumans] = useState<Record<string, string>>({});
  const [sensNhis, setSensNhis] = useState<Record<string, string>>({});
  const [listLoading, setListLoading] = useState(false);
  const [humanSearch, setHumanSearch] = useState("");
  const [nhiSearch, setNhiSearch] = useState("");
  // Server-side search results (null = not searching, show the base list)
  const [humanSearchResults, setHumanSearchResults] = useState<any[] | null>(null);
  const [nhiSearchResults, setNhiSearchResults] = useState<any[] | null>(null);
  const [searchBusy, setSearchBusy] = useState(false);

  const [view, setView] = useState<"list" | "detail">("list");
  const [selectedIdentity, setSelectedIdentity] = useState<any>(null);
  const [selectedType, setSelectedType] = useState<IdentityType>("human");
  const [detailLoading, setDetailLoading] = useState(false);

  const humanSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nhiSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const listRefreshTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  // guards the 15s auto-refresh so it doesn't clobber active search results
  const isSearchingRef = useRef(false);


const loadHumans = useCallback(async (showSpinner = false) => {
    if (showSpinner) setListLoading(true);
    try {
      const [list, hs, ns, sens] = await Promise.all([
        GatewayHumanUsersApi.list(),
        GatewayHumanUsersApi.summary().catch(() => ({})),
        GatewayNhisApi.summary().catch(() => ({})),
        GatewayPostProcessorApi.entitySensitivity().catch(() => ({})),
      ]);
      setHumans((list as any) || []);
      setHumanSummary((hs as any) || {});
      setNhiSummary((ns as any) || {});
      setSensHumans((sens as any)?.humans || {});
      setSensNhis((sens as any)?.nhis || {});
    } catch (e: any) {
      toast({ description: `Failed to load human users: ${apiError(e)}`, variant: "destructive" });
    } finally {
      setListLoading(false);
    }
  }, [toast]);


const loadNhis = useCallback(async (showSpinner = false) => {
    if (showSpinner) setListLoading(true);
    try {
      const [list, hs, ns, sens] = await Promise.all([
        GatewayNhisApi.list(),
        GatewayHumanUsersApi.summary().catch(() => ({})),
        GatewayNhisApi.summary().catch(() => ({})),
        GatewayPostProcessorApi.entitySensitivity().catch(() => ({})),
      ]);
      setNhis((list as any) || []);
      setHumanSummary((hs as any) || {});
      setNhiSummary((ns as any) || {});
      setSensHumans((sens as any)?.humans || {});
      setSensNhis((sens as any)?.nhis || {});
    } catch (e: any) {
      toast({ description: `Failed to load NHIs: ${apiError(e)}`, variant: "destructive" });
    } finally {
      setListLoading(false);
    }
  }, [toast]);


function switchListTab(tab: ListTab) {
    setListTab(tab);
    setHumanSearch("");
    setNhiSearch("");
    setHumanSearchResults(null);
    setNhiSearchResults(null);
    isSearchingRef.current = false;
    if (tab === "humans") loadHumans(true);
    else if (tab === "nhis") loadNhis(true);
  }


useEffect(() => {
    if (view !== "list") return;
    if (listRefreshTimer.current) clearInterval(listRefreshTimer.current);
    listRefreshTimer.current = setInterval(() => {
      // don't refresh the base list while a search is active — it would clobber results
      if (isSearchingRef.current) return;
      if (listTab === "humans") loadHumans();
      else if (listTab === "nhis") loadNhis();
    }, 15000);
    return () => {
      if (listRefreshTimer.current) clearInterval(listRefreshTimer.current);
    };
  }, [view, listTab, loadHumans, loadNhis]);


// Debounced server-side search for humans (min 2 chars; revert to base list otherwise)
useEffect(() => {
    if (listTab !== "humans") return;
    const q = humanSearch.trim();
    if (humanSearchTimer.current) clearTimeout(humanSearchTimer.current);
    if (q.length < 2) {
      setHumanSearchResults(null);
      setSearchBusy(false);
      isSearchingRef.current = false;
      return;
    }
    isSearchingRef.current = true;
    setSearchBusy(true);
    humanSearchTimer.current = setTimeout(async () => {
      try {
        const res: any = await GatewayHumanUsersApi.search(q);
        setHumanSearchResults(Array.isArray(res) ? res : res?.content || []);
      } catch (e: any) {
        toast({ description: `Search failed: ${apiError(e)}`, variant: "destructive" });
        setHumanSearchResults([]);
      } finally {
        setSearchBusy(false);
      }
    }, 350);
    return () => {
      if (humanSearchTimer.current) clearTimeout(humanSearchTimer.current);
    };
  }, [humanSearch, listTab, toast]);


// Debounced server-side search for NHIs
useEffect(() => {
    if (listTab !== "nhis") return;
    const q = nhiSearch.trim();
    if (nhiSearchTimer.current) clearTimeout(nhiSearchTimer.current);
    if (q.length < 2) {
      setNhiSearchResults(null);
      setSearchBusy(false);
      isSearchingRef.current = false;
      return;
    }
    isSearchingRef.current = true;
    setSearchBusy(true);
    nhiSearchTimer.current = setTimeout(async () => {
      try {
        const res: any = await GatewayNhisApi.search(q);
        setNhiSearchResults(Array.isArray(res) ? res : res?.content || []);
      } catch (e: any) {
        toast({ description: `Search failed: ${apiError(e)}`, variant: "destructive" });
        setNhiSearchResults([]);
      } finally {
        setSearchBusy(false);
      }
    }, 350);
    return () => {
      if (nhiSearchTimer.current) clearTimeout(nhiSearchTimer.current);
    };
  }, [nhiSearch, listTab, toast]);


useEffect(() => {
    loadHumans(true);
    GatewayNhisApi.summary().then((ns: any) => setNhiSummary(ns || {})).catch(() => {});
  }, [loadHumans]);


// Server-side search results take over when active; otherwise show the base list
  const filteredHumans = humanSearchResults != null ? humanSearchResults : humans;
  const filteredNhis = nhiSearchResults != null ? nhiSearchResults : nhis;


async function showDetail(id: string, type: IdentityType) {
    setDetailLoading(true);
    setView("detail");
    setSelectedType(type);
    try {
      const api = type === "human" ? GatewayHumanUsersApi : GatewayNhisApi;
      const identity = await api.get(id);
      setSelectedIdentity(identity);
    } catch (e: any) {
      toast({ description: `Failed to load identity: ${apiError(e)}`, variant: "destructive" });
      setView("list");
    } finally {
      setDetailLoading(false);
    }
  }


function backToList() {
    setView("list");
    setSelectedIdentity(null);
    if (listTab === "humans") loadHumans(true);
    else if (listTab === "nhis") loadNhis(true);
  }


async function onStatusChange() {
    if (!selectedIdentity) return;
    const api = selectedType === "human" ? GatewayHumanUsersApi : GatewayNhisApi;
    const updated = await api.get(selectedIdentity.id).catch(() => null);
    if (updated) setSelectedIdentity(updated);
    if (selectedType === "human") loadHumans();
    else loadNhis();
  }


const pendingHumans = humans.filter((h) => h.status === "PENDING").length;
  const pendingNhis = nhis.filter((n) => n.status === "PENDING").length;

  const activeNowTotal = (humanSummary.activeNow ?? 0) + (nhiSummary.activeNow ?? 0);


if (view === "detail") {
    if (detailLoading || !selectedIdentity) {
      return (
        <div className="flex items-center gap-2 py-16 justify-center text-muted-foreground text-sm">
          <RefreshCw className="w-5 h-5 animate-spin" /> Loading identity…
        </div>
      );
    }
    return (
      <div className="ag-page">
        <DetailView
          identity={selectedIdentity}
          identityType={selectedType}
          onBack={backToList}
          onStatusChange={onStatusChange}
        />
      </div>
    );
  }


return (
    <div className="ag-page">
      <div className="ag-card ag-toolbar ag-nav">
        <div className="cea-tabs ag-tabs" role="tablist" aria-label="Identity lists">
          {(
            [
              { id: "humans" as ListTab, label: "Humans", icon: <Users className="w-3.5 h-3.5" />, count: humanSummary.totalHumans },
              { id: "nhis" as ListTab, label: "NHIs", icon: <Bot className="w-3.5 h-3.5" />, count: nhiSummary.totalNhis },
              { id: "active" as ListTab, label: "Active Now", icon: <Zap className="w-3.5 h-3.5" />, count: activeNowTotal > 0 ? activeNowTotal : undefined },
            ] as const
          ).map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={listTab === t.id}
              className={cn("cea-tab", listTab === t.id && "active")}
              onClick={() => switchListTab(t.id)}
            >
              {t.icon}
              {t.label}
              {t.count != null && t.count > 0 && (
                <span className="ag-tab-count">{t.count}</span>
              )}
            </button>
          ))}
        </div>
        <div className="ag-toolbar-group">
          {(pendingHumans + pendingNhis) > 0 && (
            <span className="ag-live" data-tone="amber">
              <span className="ag-live-dot animate-pulse" aria-hidden="true" />
              {pendingHumans + pendingNhis} pending approval
            </span>
          )}
          {listTab === "humans" && (
            <div className="ag-search ag-search-wide">
              <Search aria-hidden="true" />
              <input
                type="text"
                placeholder="Search by name, username or email…"
                value={humanSearch}
                onChange={(e) => setHumanSearch(e.target.value)}
              />
              {searchBusy && <RefreshCw className="ag-search-busy animate-spin" aria-hidden="true" />}
            </div>
          )}
          {listTab === "nhis" && (
            <div className="ag-search ag-search-wide">
              <Search aria-hidden="true" />
              <input
                type="text"
                placeholder="Search by service name or client ID…"
                value={nhiSearch}
                onChange={(e) => setNhiSearch(e.target.value)}
              />
              {searchBusy && <RefreshCw className="ag-search-busy animate-spin" aria-hidden="true" />}
            </div>
          )}
          <Button
            variant="outline"
            className="ag-button no-default-hover-elevate"
            onClick={() => listTab === "humans" ? loadHumans(true) : loadNhis(true)}
            disabled={listLoading}
          >
            <RefreshCw className={cn(listLoading && "animate-spin")} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="ag-stat-grid">
        <GatewayStatCard
          icon={<Users />}
          label={listTab === "nhis" ? "Total NHIs" : "Total Humans"}
          value={listTab === "nhis" ? (nhiSummary.totalNhis ?? 0) : (humanSummary.totalHumans ?? 0)}
          colorClass="text-primary"
        />
        <GatewayStatCard
          icon={<Activity />}
          label="Active Today"
          value={listTab === "nhis" ? (nhiSummary.activeToday ?? 0) : (humanSummary.activeToday ?? 0)}
          colorClass="text-emerald-600 dark:text-emerald-400"
        />
        <GatewayStatCard
          icon={<Zap />}
          label="Active Now"
          value={listTab === "nhis" ? (nhiSummary.activeNow ?? 0) : (humanSummary.activeNow ?? 0)}
          colorClass="text-sky-600 dark:text-sky-400"
        />
        <GatewayStatCard
          icon={<ShieldOff />}
          label="Blocked"
          value={listTab === "nhis" ? (nhiSummary.blocked ?? 0) : (humanSummary.blocked ?? 0)}
          colorClass={
            ((listTab === "nhis" ? nhiSummary.blocked : humanSummary.blocked) ?? 0) > 0
              ? "text-red-600 dark:text-red-400"
              : "text-muted-foreground"
          }
        />
      </div>

      <div className="ag-card ag-content">
        <div className="ag-scroll">
          {listTab === "humans" && (
            listLoading ? (
              <div className="ag-loading">
                <span className="ag-spinner animate-spin" aria-hidden="true" />
                <span>Loading…</span>
              </div>
            ) : filteredHumans.length === 0 ? (
              <div className="ag-empty h-full">
                <span className="ag-empty-icon" aria-hidden="true">
                  <Users />
                </span>
                <p>
                  {humanSearchResults != null
                    ? `No results for "${humanSearch.trim()}"`
                    : "No human users discovered yet. Humans appear when they authenticate via OAuth2 delegated access."}
                </p>
              </div>
            ) : (
              <div className="ag-server-list">
                {filteredHumans.map((h: any) => (
                  <IdentityCard
                    key={h.id}
                    identity={h}
                    type="human"
                    sensitivity={sensHumans[h.preferredUsername]}
                    onClick={() => showDetail(h.id, "human")}
                  />
                ))}
              </div>
            )
          )}

          {listTab === "nhis" && (
            listLoading ? (
              <div className="ag-loading">
                <span className="ag-spinner animate-spin" aria-hidden="true" />
                <span>Loading…</span>
              </div>
            ) : filteredNhis.length === 0 ? (
              <div className="ag-empty h-full">
                <span className="ag-empty-icon" aria-hidden="true">
                  <Bot />
                </span>
                <p>
                  {nhiSearchResults != null
                    ? `No results for "${nhiSearch.trim()}"`
                    : "No non-human identities discovered yet. NHIs appear when automated agents authenticate via API keys or client credentials."}
                </p>
              </div>
            ) : (
              <div className="ag-server-list">
                {filteredNhis.map((n: any) => (
                  <IdentityCard
                    key={n.id}
                    identity={n}
                    type="nhi"
                    sensitivity={sensNhis[n.id]}
                    onClick={() => showDetail(n.id, "nhi")}
                  />
                ))}
              </div>
            )
          )}

          {listTab === "active" && <ActiveNowContent />}
        </div>
      </div>
    </div>
  );
}
